import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { UserCircle, X, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";
import { get as getShiftOwner, set as setShiftOwner, clear as clearShiftOwner, subscribe } from "@/lib/shiftOwner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * Header chip that shows / manages the "clock-in" of the physical worker
 * currently operating this device. Auto-opens the picker when kiosk mode is
 * activated and no owner is set — so a supervisor handing a tablet to a
 * miner never forgets to assign it.
 */
export default function ShiftOwnerChip() {
  const { t } = useLang();
  const [owner, setOwner] = useState(getShiftOwner());
  const [open, setOpen] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => subscribe(setOwner), []);

  // Auto-open on kiosk activation if there's no active owner.
  useEffect(() => {
    const onKiosk = (e) => {
      if (e.detail?.active && !getShiftOwner()) {
        setOpen(true);
      }
    };
    window.addEventListener("kiosk-toggle", onKiosk);
    return () => window.removeEventListener("kiosk-toggle", onKiosk);
  }, []);

  const loadDirectory = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/directory");
      setDirectory(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el directorio de personal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && directory.length === 0) loadDirectory();
  }, [open, directory.length]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return directory.slice(0, 60);
    return directory.filter((e) =>
      (e.name || "").toLowerCase().includes(q) ||
      (e.position || "").toLowerCase().includes(q) ||
      (e.crew || "").toLowerCase().includes(q) ||
      (e.dni || "").toLowerCase().includes(q)
    ).slice(0, 60);
  }, [directory, query]);

  const assign = (emp) => {
    setShiftOwner(emp);
    setOpen(false);
    toast.success(t("shiftOwnerAssigned", { name: emp.name }));
  };

  const endShift = () => {
    if (!owner) return;
    clearShiftOwner();
    toast.info(t("shiftOwnerCleared"));
  };

  const downloadShiftXlsx = async () => {
    if (!owner) return;
    try {
      const params = { shift_owner_id: owner.id, started_at: owner.started_at };
      const r = await api.get("/operations/shift-report", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `turno-${(owner.name || "operador").replace(/\s+/g, "_")}-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("shiftOwnerReportDownloaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar el reporte");
    }
  };

  return (
    <>
      <button
        data-testid="shift-owner-chip"
        onClick={() => setOpen(true)}
        title={owner ? t("shiftOwnerChipActiveTitle") : t("shiftOwnerChipEmptyTitle")}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold border transition-colors touch-manipulation ${
          owner
            ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
            : "bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200"
        }`}
      >
        <UserCircle size={14} weight="bold" />
        <span data-testid="shift-owner-chip-label" className="max-w-[140px] truncate">
          {owner ? owner.name : t("shiftOwnerEmpty")}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="shift-owner-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-xl">{t("shiftOwnerDialogTitle")}</DialogTitle>
            <DialogDescription className="text-sm">{t("shiftOwnerDialogHint")}</DialogDescription>
          </DialogHeader>

          {owner && (
            <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-md text-xs">
              <div>
                <div className="font-bold text-emerald-900">{owner.name}</div>
                <div className="text-emerald-800/80">
                  {owner.position || "—"}
                  {owner.crew ? ` · ${owner.crew}` : ""}
                </div>
                <div className="text-[10px] text-emerald-800/60 mt-1">
                  {t("shiftOwnerStartedAt")}: {new Date(owner.started_at).toLocaleString()}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                data-testid="shift-owner-end-shift"
                onClick={endShift}
                className="text-red-700 border-red-200 hover:bg-red-50"
              >
                <X size={12} weight="bold" className="mr-1" /> {t("shiftOwnerEndShift")}
              </Button>
            </div>
          )}

          {owner && (
            <Button
              variant="outline"
              size="sm"
              data-testid="shift-owner-download-report"
              onClick={downloadShiftXlsx}
              className="w-full gap-1.5 text-xs"
            >
              <DownloadSimple size={13} weight="bold" /> {t("shiftOwnerReportBtn")}
            </Button>
          )}

          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="shift-owner-search"
              placeholder={t("shiftOwnerSearchPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
            {loading && <p className="text-xs text-slate-500 p-4 text-center">{t("loading")}</p>}
            {!loading && filtered.length === 0 && (
              <p className="text-xs text-slate-500 p-4 text-center">{t("shiftOwnerNoMatches")}</p>
            )}
            {!loading && filtered.map((emp) => (
              <button
                key={emp.id}
                data-testid={`shift-owner-pick-${emp.id}`}
                onClick={() => assign(emp)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-blue-50 transition-colors"
              >
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-900 font-black text-xs">
                  {(emp.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{emp.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {emp.position || "—"}
                    {emp.crew ? ` · ${emp.crew}` : ""}
                    {emp.dni ? ` · DNI ${emp.dni}` : ""}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
